/**
 * useCortexStream.js
 * ==================
 * React custom hook that consumes SSE events from the
 * /api/cortex/query/stream endpoint and progressively
 * updates message state.
 *
 * Usage in your chat component:
 *
 *   const { streamQuery, isStreaming, streamingMessage } = useCortexStream();
 *
 *   // When user submits a question:
 *   const result = await streamQuery({
 *     question,
 *     conversation_id,
 *     cortex_thread_id,
 *     parent_message_id,
 *     token,           // JWT auth token
 *     skill_id,        // optional
 *   });
 *
 *   // result is the final 'done' payload (same shape as non-streaming JSON)
 *   // streamingMessage updates in real-time as events arrive
 *
 * The hook handles:
 *   - fetch() with ReadableStream (NOT EventSource — we need POST + headers)
 *   - Progressive state updates for text, thinking, status, charts
 *   - Heartbeat handling (SSE comments)
 *   - Error recovery
 *   - Abort on unmount
 */

import { useState, useRef, useCallback } from 'react';

// ════════════════════════════════════════════════════════════════
// SSE LINE PARSER
// Parses raw text chunks from fetch ReadableStream into SSE events.
// Handles the fact that chunks may split across event boundaries.
// Uses a persistent buffer so events that span multiple chunks
// are reassembled correctly before parsing.
// ════════════════════════════════════════════════════════════════

class SSEParser {
  constructor() {
    this.buffer = '';
    this.currentEvent = null;
    this.currentData = '';
  }

  /**
   * Feed a new chunk into the parser and return any complete events.
   * Events that span chunk boundaries are held until complete.
   */
  feed(chunk) {
    this.buffer += chunk;
    const events = [];

    // Process complete lines (terminated by \n)
    while (true) {
      const newlineIdx = this.buffer.indexOf('\n');
      if (newlineIdx === -1) break; // no complete line yet

      const line = this.buffer.substring(0, newlineIdx).trim();
      this.buffer = this.buffer.substring(newlineIdx + 1);

      // SSE comment (heartbeat) — skip
      if (line.startsWith(':')) continue;

      // Empty line = end of current event
      if (line === '') {
        if (this.currentEvent && this.currentData) {
          try {
            events.push({
              type: this.currentEvent,
              data: JSON.parse(this.currentData),
            });
          } catch {
            // Malformed JSON — skip this event
            console.warn('[SSE] Failed to parse event data:', this.currentEvent, this.currentData.substring(0, 100));
          }
        }
        this.currentEvent = null;
        this.currentData = '';
        continue;
      }

      if (line.startsWith('event:')) {
        this.currentEvent = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        // Append (SSE spec allows multiple data: lines per event)
        this.currentData += line.slice(5).trim();
      }
    }

    return events;
  }

  /**
   * Call when the stream ends to flush any remaining buffered event.
   */
  flush() {
    const events = [];
    if (this.currentEvent && this.currentData) {
      try {
        events.push({
          type: this.currentEvent,
          data: JSON.parse(this.currentData),
        });
      } catch {
        console.warn('[SSE] Failed to parse final event:', this.currentEvent, this.currentData.substring(0, 100));
      }
      this.currentEvent = null;
      this.currentData = '';
    }
    this.buffer = '';
    return events;
  }
}


// ════════════════════════════════════════════════════════════════
// HOOK
// ════════════════════════════════════════════════════════════════

export default function useCortexStream(apiBaseUrl = '') {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState(null);
  const abortRef = useRef(null);

  /**
   * Abort any in-flight stream (e.g. on unmount or new query).
   */
  const abortStream = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  /**
   * Execute a streaming query against the Cortex Agent.
   *
   * @param {Object} params
   * @param {string} params.question
   * @param {string} params.conversation_id
   * @param {string} params.cortex_thread_id
   * @param {number} params.parent_message_id
   * @param {string} params.token          — JWT Bearer token
   * @param {string} [params.skill_id]     — optional skill override
   *
   * @returns {Promise<Object>} — the final 'done' payload, or null on error
   */
  const streamQuery = useCallback(async (params) => {
    const {
      question,
      conversation_id,
      cortex_thread_id,
      parent_message_id,
      token,
      skill_id,
    } = params;

    // Abort any previous stream
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    // Initialize streaming message state
    const initialMessage = {
      content: '',
      queryEngine: 'cortex',
      cortexData: {
        thinking_steps: [],
        charts: [],
        status_steps: [],
      },
      isStreaming: true,
    };

    setStreamingMessage(initialMessage);
    setIsStreaming(true);

    // Mutable accumulators (we update React state from these)
    let textParts = [];
    let thinkingSteps = [];
    let currentThinkingBuf = '';
    let statusSteps = [];
    let charts = [];
    let finalResult = null;

    try {
      const response = await fetch(
        `${apiBaseUrl}/api/cortex/query/stream`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            question,
            conversation_id,
            cortex_thread_id,
            parent_message_id,
            ...(skill_id ? { skill_id } : {}),
          }),
          signal: controller.signal,
        }
      );

      // ── Non-200: likely a JSON error (cap exceeded, etc.) ──
      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const errorJson = await response.json();
          setIsStreaming(false);
          setStreamingMessage(null);
          return { error: true, ...errorJson };
        }
        throw new Error(`HTTP ${response.status}`);
      }

      // ── Check if response is actually SSE ─────────────────
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('text/event-stream')) {
        // Backend returned JSON (streaming disabled? fallback?)
        const jsonResult = await response.json();
        setIsStreaming(false);
        setStreamingMessage(null);
        return jsonResult;
      }

      // ── Read the SSE stream ───────────────────────────────
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      const parser = new SSEParser();

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          // Flush any remaining buffered event (e.g. done event
          // without a trailing blank line)
          const remaining = parser.flush();
          for (const event of remaining) {
            // Process exactly like the main loop below
            if (event.type === 'cached' || event.type === 'done') {
              finalResult = event.data;
              setStreamingMessage(prev => ({
                ...prev,
                content: event.data.response || prev.content,
                isStreaming: false,
                cortexData: event.data.cortex_data || prev.cortexData,
              }));
            }
          }
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const events = parser.feed(chunk);

        for (const event of events) {
          switch (event.type) {

            // ── Answer text (streaming in) ──────────────────
            case 'response.text.delta': {
              const text = event.data.text || '';
              if (text) {
                textParts.push(text);
                setStreamingMessage(prev => ({
                  ...prev,
                  content: textParts.join(''),
                }));
              }
              break;
            }

            case 'response.text': {
              const text = event.data.text || '';
              if (text && textParts.length === 0) {
                textParts.push(text);
                setStreamingMessage(prev => ({
                  ...prev,
                  content: text,
                }));
              }
              break;
            }

            // ── Thinking (streaming in) ─────────────────────
            case 'response.thinking.delta': {
              const text = event.data.text || '';
              if (text) {
                currentThinkingBuf += text;
                // Show in-progress thinking as the last step
                const displaySteps = [
                  ...thinkingSteps,
                  currentThinkingBuf,
                ];
                setStreamingMessage(prev => ({
                  ...prev,
                  cortexData: {
                    ...prev.cortexData,
                    thinking_steps: displaySteps,
                  },
                }));
              }
              break;
            }

            case 'response.thinking': {
              // Flush current buffer as a completed step
              if (currentThinkingBuf) {
                thinkingSteps.push(currentThinkingBuf.trim());
                currentThinkingBuf = '';
              }
              const text = event.data.text || '';
              if (text) {
                thinkingSteps.push(text.trim());
              }
              setStreamingMessage(prev => ({
                ...prev,
                cortexData: {
                  ...prev.cortexData,
                  thinking_steps: [...thinkingSteps],
                },
              }));
              break;
            }

            // ── Status updates ──────────────────────────────
            case 'response.status': {
              const msg = event.data.message || '';
              if (msg && !statusSteps.includes(msg)) {
                statusSteps.push(msg);
                setStreamingMessage(prev => ({
                  ...prev,
                  cortexData: {
                    ...prev.cortexData,
                    status_steps: [...statusSteps],
                  },
                }));
              }
              break;
            }

            // ── Charts ──────────────────────────────────────
            case 'response.chart': {
              const chartSpec = event.data.chart_spec;
              if (chartSpec) {
                charts.push(chartSpec);
                setStreamingMessage(prev => ({
                  ...prev,
                  cortexData: {
                    ...prev.cortexData,
                    charts: [...charts],
                  },
                }));
              }
              break;
            }

            // ── Cached response (instant — no streaming) ────
            case 'cached': {
              finalResult = event.data;
              setStreamingMessage({
                content: event.data.response || '',
                queryEngine: 'cortex',
                cortexData: event.data.cortex_data || {
                  thinking_steps: [],
                  charts: [],
                  status_steps: [],
                },
                isStreaming: false,
              });
              break;
            }

            // ── Done (final metadata) ───────────────────────
            case 'done': {
              finalResult = event.data;
              // Finalize the message with server-processed text
              // (includes markdown table fixes, etc.)
              setStreamingMessage(prev => ({
                ...prev,
                content: event.data.response || prev.content,
                isStreaming: false,
                cortexData: event.data.cortex_data || prev.cortexData,
              }));
              break;
            }

            // ── Error ───────────────────────────────────────
            case 'error': {
              console.error('[CortexStream] Server error:', event.data.error);
              setStreamingMessage(prev => ({
                ...prev,
                content: prev.content || event.data.error || 'An error occurred.',
                isStreaming: false,
                error: event.data.error,
              }));
              finalResult = { error: true, ...event.data };
              break;
            }

            default:
              // metadata and other events — no UI update needed
              break;
          }
        }
      }

    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('[CortexStream] Stream aborted');
      } else {
        console.error('[CortexStream] Stream error:', err);
        setStreamingMessage(prev => ({
          ...(prev || initialMessage),
          content: prev?.content || 'Connection lost. Please try again.',
          isStreaming: false,
          error: err.message,
        }));
        finalResult = { error: true, message: err.message };
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }

    return finalResult;
  }, [apiBaseUrl]);

  return {
    streamQuery,
    isStreaming,
    streamingMessage,
    abortStream,
    setStreamingMessage,
  };
}
